import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, ShieldCheck, RefreshCw, Eye, EyeOff, Check } from "lucide-react";
import { api } from "@/lib/api";

// Owner-only password reset flow.
// Step 1 (pick): fetch the two allow-listed recipients (masked) and let the
// owner choose which mailbox should receive the OTP.
// Step 2 (verify): 6-digit OTP + a new password. Backend enforces expiry,
// attempts, hashing and rate-limit.
// Step 3 (done): confirmation → back to login.

const PW_RULES = [
  { id: "len", label: "At least 10 characters", test: (v) => v.length >= 10 },
  { id: "upper", label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { id: "lower", label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { id: "digit", label: "One number", test: (v) => /\d/.test(v) },
];

export default function AdminForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState("pick"); // pick | verify | done
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedMasked, setSelectedMasked] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/forgot-password/options");
        setOptions(Array.isArray(r.data) ? r.data : []);
      } catch {
        toast.error("Could not load reset options. Try again shortly.");
      }
    })();
  }, []);

  const requestOtp = async (optionId, masked) => {
    setBusy(true);
    try {
      await api.post("/auth/forgot-password/send", { option_id: optionId });
      setSelectedOption(optionId);
      setSelectedMasked(masked);
      setStep("verify");
      toast.success(`OTP sent to ${masked}. Check your inbox.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code.");
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    if (!selectedOption) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password/send", { option_id: selectedOption });
      toast.success(`A new code was sent to ${selectedMasked}.`);
      setOtp("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code."); return; }
    if (!PW_RULES.every((r) => r.test(newPassword))) {
      toast.error("New password does not meet the requirements.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { otp, new_password: newPassword });
      setStep("done");
      toast.success("Password updated. Sign in with your new password.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Request a new code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-propertic-bg" data-testid="forgot-password-page">
      <div className="hidden lg:block relative bg-propertic-navy">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.25), transparent 60%), radial-gradient(circle at 70% 70%, rgba(43,75,119,0.5), transparent 55%)"
        }} />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div>
            <div className="inline-flex items-center bg-white rounded-xl px-3 py-2">
              <img src="/propertic-logo.png" alt="The Propertic" className="h-12 w-auto object-contain" />
            </div>
            <p className="mt-3 font-mono-brand text-propertic-gold-2/90">CRM Console</p>
          </div>
          <div>
            <p className="font-mono-brand text-propertic-gold mb-3">Reset your password</p>
            <p className="font-editorial text-4xl leading-tight max-w-md">
              Two mailboxes. <br />
              <span className="text-propertic-gold italic">One code. Belt and suspenders.</span>
            </p>
            <p className="mt-8 text-white/70 text-sm max-w-md leading-relaxed">
              Pick which of your two trusted mailboxes should receive the one-time reset code. If one inbox is ever compromised, the other still lets you back in.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link
            to="/admin/login"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted hover:text-propertic-gold transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign-in
          </Link>

          {step === "pick" && (
            <div data-testid="forgot-step-pick">
              <h1 className="font-editorial text-3xl mt-6 text-propertic-navy">Where should we send the code?</h1>
              <p className="text-[12px] text-propertic-muted mt-2 leading-relaxed">
                Choose one of your allow-listed mailboxes. A 6-digit code will arrive within a minute.
              </p>

              <div className="mt-6 space-y-3">
                {options.length === 0 && (
                  <div className="text-[12px] text-propertic-muted font-mono-brand" data-testid="options-loading">Loading…</div>
                )}
                {options.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => requestOtp(opt.id, opt.masked)}
                    disabled={busy}
                    data-testid={`reset-option-${opt.id}`}
                    className="w-full flex items-center justify-between gap-3 rounded-2xl border hairline bg-white h-14 px-4 hover:border-propertic-gold hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-propertic-gold" />
                      <span className="font-mono-brand text-propertic-navy text-sm tabular">{opt.masked}</span>
                    </div>
                    <span className="text-[10px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted">
                      Send code
                    </span>
                  </button>
                ))}
              </div>

              <p className="mt-6 text-[11px] text-propertic-muted leading-relaxed">
                For your safety, we never show the full address, only the last character. If neither mailbox looks familiar, you're not the owner.
              </p>
            </div>
          )}

          {step === "verify" && (
            <form onSubmit={submitReset} className="mt-4" data-testid="forgot-step-verify">
              <h1 className="font-editorial text-3xl mt-6 text-propertic-navy">Enter the code</h1>
              <div className="mt-4 rounded-2xl bg-propertic-sand/60 border border-propertic-gold/30 p-3.5 text-[12px] text-propertic-navy leading-relaxed flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-propertic-gold mt-0.5 shrink-0" />
                <div>
                  We sent a 6-digit code to <span className="font-mono-brand text-propertic-gold">{selectedMasked}</span>.
                  It expires in 15 minutes.
                </div>
              </div>

              <label className="block mt-5">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">6-digit code</span>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  autoComplete="one-time-code"
                  data-testid="reset-otp-input"
                  placeholder="••••••"
                  className="mt-2 w-full bg-white rounded-xl border hairline h-12 px-4 text-propertic-navy text-lg text-center tracking-[0.5em] font-editorial focus:border-propertic-gold outline-none"
                />
              </label>

              <label className="block mt-4">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">New password</span>
                <div className="relative mt-2">
                  <input
                    type={showPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    data-testid="reset-new-password"
                    className="w-full bg-white rounded-xl border hairline h-11 px-4 pr-11 text-propertic-navy text-sm focus:border-propertic-gold outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-propertic-muted hover:text-propertic-navy"
                    data-testid="reset-toggle-visibility"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <ul className="mt-3 space-y-1.5" data-testid="reset-password-rules">
                {PW_RULES.map((r) => {
                  const ok = r.test(newPassword);
                  return (
                    <li key={r.id} className={`text-[11px] flex items-center gap-2 ${ok ? "text-emerald-700" : "text-propertic-muted"}`}>
                      <Check className={`w-3 h-3 ${ok ? "opacity-100" : "opacity-30"}`} />
                      {r.label}
                    </li>
                  );
                })}
              </ul>

              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className="btn-gold rounded-full w-full h-11 mt-5 text-xs font-mono-brand tracking-widest disabled:opacity-60"
              >
                {busy ? "…" : "Update password"}
              </button>

              <button
                type="button"
                onClick={resendOtp}
                disabled={busy}
                data-testid="reset-resend"
                className="mt-3 w-full text-[11px] text-propertic-muted hover:text-propertic-navy inline-flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="w-3 h-3" /> Resend code
              </button>
              <button
                type="button"
                onClick={() => { setStep("pick"); setOtp(""); setNewPassword(""); }}
                className="mt-2 w-full text-[11px] text-propertic-muted hover:text-propertic-navy"
                data-testid="reset-change-mailbox"
              >
                ← Send to a different mailbox
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="mt-6" data-testid="forgot-step-done">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
                <div className="flex items-center gap-2 text-emerald-800">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-editorial text-lg">Password updated</span>
                </div>
                <p className="text-[12px] text-emerald-900/80 mt-2 leading-relaxed">
                  You can now sign in with your new password. Any existing sessions will remain until they expire, sign out on other devices if you're unsure.
                </p>
              </div>
              <button
                onClick={() => nav("/admin/login")}
                data-testid="reset-back-to-signin"
                className="btn-gold rounded-full w-full h-11 mt-5 text-xs font-mono-brand tracking-widest"
              >
                Back to sign-in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
